import {messages} from '@smartmixin/i18n';
import {MessageDescriptor} from 'react-intl';
import {DateTimeConfig, formatDate, hourFormatter} from '../Dates';
import {
  convertValueToString,
  getPolarAngleFromDegrees,
  getPressureUnitLabel,
  getRainUnitLabel,
  getTemperatureUnitLabel,
  getWindUnitLabel,
  Units,
} from '../Units';
import dewPoint from '../Weather/dewPoint';
import feelsLike from '../Weather/apparentTemperature/feelsLike';
import {CurrentConditions} from './currentConditions';
import {DayRange} from './dayRange';

type FormatMessage = (
  descriptor: MessageDescriptor,
  values?: Record<string, string>,
) => string;

export type CurrentConditionsViewOptions = {
  units: Units;
  /** The place's, not the device's: the times on the track are local to it. */
  timezone: string;
  dateTimeConfig: DateTimeConfig;
  /** `selectUserFeelLike`. */
  feelLikeFormula: 0 | 1 | 2;
  formatMessage: FormatMessage;
};

type Reading = {value: string; unit: string};

export type DayRangeEnd = {
  /** "12°" */
  value: string;
  /** In the user's hour format, in the place's timezone. */
  time: string;
  reached: boolean;
  /** "12° at 06:00" or "21° expected 15:00". */
  text: string;
};

export type CurrentConditionsView = {
  icon: number;
  /** Whole degrees: it is a forecast, and a decimal would claim otherwise. */
  temperature: Reading;
  /** `text` is the hero's line, e.g. "Feels like 11°". */
  feelsLike: Reading & {text: string};
  range:
    | {
        kind: 'flat';
        /** The reading with its unit, e.g. "9°C". */
        current: string;
        /** "Steady 9°C all day". */
        text: string;
      }
    | {
        kind: 'range';
        /** Fractions for the track; colours are each app's to draw. */
        range: DayRange;
        min: DayRangeEnd;
        max: DayRangeEnd;
      };
  wind: Reading & {
    /** No wind, so no direction to draw. */
    calm: boolean;
    /** Degrees, for the arrow. */
    direction: number;
    /** The compass point in words, null when calm or unknown. */
    compass: string | null;
    /** "Gust 34 km/h". */
    gust: string;
  };
  humidity: Reading;
  dewPoint: Reading;
  /**
   * `stable` is deliberately null: the Help Centre promises an arrow only when
   * the pressure has risen or fallen noticeably.
   */
  pressure: Reading & {trend: 'up' | 'down' | null};
  /** So far today. */
  rain: Reading;
};

/**
 * The strings a place's "Current conditions" card shows, for both apps: each
 * renders this bag its own way, and neither formats a number itself.
 */
export const currentConditionsView = (
  {current, range, rainSoFar, pressureTrend}: CurrentConditions,
  {
    units,
    timezone,
    dateTimeConfig,
    feelLikeFormula,
    formatMessage,
  }: CurrentConditionsViewOptions,
): CurrentConditionsView => {
  const temperatureUnit = getTemperatureUnitLabel(units);
  const windUnit = getWindUnitLabel(units);
  const whole = (celsius: number) =>
    convertValueToString(celsius, 'Temperature', units, 0);
  const degrees = (celsius: number) => `${whole(celsius)}°`;
  const clock = (timestamp: number) =>
    formatDate(timestamp, timezone, hourFormatter(dateTimeConfig));

  const end = (value: number, at: number, reached: boolean): DayRangeEnd => {
    const formatted = {value: degrees(value), time: clock(at)};
    return {
      ...formatted,
      reached,
      text: formatMessage(
        reached
          ? messages.dashboard.dayRangeReached
          : messages.dashboard.dayRangeExpected,
        formatted,
      ),
    };
  };

  const feels = feelsLike(feelLikeFormula, current.T, current.U, current.FF);
  const angle = getPolarAngleFromDegrees(current.DD);
  const calm = current.FF === 0;
  const currentWithUnit = `${whole(current.T)}${temperatureUnit}`;

  return {
    icon: current.ICON,
    temperature: {value: whole(current.T), unit: temperatureUnit},
    feelsLike: {
      value: whole(feels),
      unit: temperatureUnit,
      text: `${formatMessage(messages.metrics.feelsLike)} ${degrees(feels)}`,
    },
    range: range.isFlat
      ? {
          kind: 'flat',
          current: currentWithUnit,
          text: formatMessage(messages.dashboard.dayRangeSteady, {
            value: currentWithUnit,
          }),
        }
      : {
          kind: 'range',
          range,
          min: end(range.min, range.minAt, range.minReached),
          max: end(range.max, range.maxAt, range.maxReached),
        },
    wind: {
      value: convertValueToString(current.FF, 'Wind', units),
      unit: windUnit,
      calm,
      direction: current.DD,
      compass: calm || !angle ? null : formatMessage(messages.compass[angle]),
      gust: `${formatMessage(messages.metrics.gust)} ${convertValueToString(
        current.FXY,
        'Wind',
        units,
      )} ${windUnit}`,
    },
    humidity: {
      value: convertValueToString(current.U, 'Humidity', units),
      unit: '%',
    },
    dewPoint: {
      value: whole(dewPoint(current.T, current.U)),
      unit: temperatureUnit,
    },
    pressure: {
      value: convertValueToString(current.SLP, 'Pressure', units),
      unit: getPressureUnitLabel(units),
      trend:
        pressureTrend === 'up' || pressureTrend === 'down'
          ? pressureTrend
          : null,
    },
    rain: {
      value: convertValueToString(rainSoFar, 'Rain', units),
      unit: getRainUnitLabel(units),
    },
  };
};
